import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Row, Col } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { getSalutationOptions, getGenderForSalutation } from 'helpers/clients';
import { formatEntityErrorsForDisplay } from 'helpers/formStatus';
import { clientSingleOptionFormatter } from 'components/forms/autocomplete/Client';
import Tabs from 'components/layout/Tabs';
import Loader from 'components/layout/Loader';
import ClientProfile from 'components/layout/client/ClientProfile';
import ClientFinancial from 'components/layout/client/ClientFinancial';
import ClientRoles from 'components/layout/client/ClientRoles';
import Data from 'components/Data';
import Entity from 'components/Entity';
import FormInput from 'components/forms/FormInput';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormHoldingCompany from 'components/forms/FormHoldingCompany';
import FormErrors from 'components/forms/FormErrors';
import { entityMetaMapping, DEFAULT_USER_ROLES } from 'js/constants';
import may from 'js/security';
import FormSwitchery from 'components/forms/FormSwitchery';
import List from 'components/List';

class UserForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      entity: null,
      entityLoaded: false,
      entityChanged: false,
      parent: null,
      sendInvitation: false,
    };

    this.entity = new Entity();

    // if we get user data from the outside, we can pass it on to our data entity
    const data = props.data || { has_accepted_terms_and_conditions: false };

    // if we don't have roles, we need to set defaults
    if (data.roles === undefined) {
      data.roles = DEFAULT_USER_ROLES;
    }
    // if we don't have profile information, we need to set defaults
    if (data.profile === undefined) {
      const { country, timezone } = props.user.profile;
      const companyDefault = '';
      data.profile = {
        company: companyDefault,
        country,
        timezone,
        salutation: 'mr',
        gender: 'male',
      };
    }
    // if we don't have financial information, we need to set defaults
    if (data.financial === undefined) {
      data.financial = {
        currency: props.user.financial.currency,
        vat_id_name: props.user.financial.vat_id_name,
        vat_rates: props.user.financial.vat_rates,
      };
    }

    this.entity.setData(data);

    if (props.meta[entityMetaMapping.client] !== undefined && !props.meta[entityMetaMapping.client].pending) {
      this.entity.setMeta(props.meta, entityMetaMapping.client);

      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }
  }

  updateParent = (fieldName, parent) => {
    this.setState({ parent });
  };

  changeEntity = (entity, callback = null) => {
    this.setState({ entity, entityChanged: true }, callback);
  };

  updateEntity = (propertyName, value, fieldTree = []) => {
    const newEntity = { ...this.state.entity.updateValue(propertyName, value, fieldTree) };

    this.changeEntity(newEntity);
  };

  updateSalutation = (name, value) => {
    const { entity } = this.state;

    const gender = getGenderForSalutation(value);

    let newEntity = { ...entity.updateValue('salutation', value, ['profile']) };
    newEntity = { ...entity.updateValue('gender', gender, ['profile']) };
    this.changeEntity(newEntity);
  };

  componentDidMount = () => {
    if (this.props.clientData !== undefined && this.props.clientData !== false) {
      this.updateParent('parent', this.props.clientData);
      this.updateEntity('company', this.props.clientData.profile?.company, ['profile']);
    }
  };

  componentDidUpdate = () => {
    const { meta } = this.props;

    if (
      meta[entityMetaMapping.client] !== undefined &&
      !meta[entityMetaMapping.client].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.client);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }
  };

  formSubmit = e => {
    e.preventDefault();

    this.setState(
      prevState => ({ entity: prevState.entity.validate() }),
      () => {
        const { onSubmit } = this.props;
        const { entity, parent, sendInvitation } = this.state;

        if (entity.isValid) {
          onSubmit({
            ...entity.processedData,
            parent,
            sendInvitation: !!(entity.getProfile().getEmail() && sendInvitation),
          });
        }
      }
    );
  };

  render() {
    const {
      intl: { messages },
      formStatus,
      user,
      type,
      languages,
    } = this.props;
    const { entity, entityChanged, entityLoaded, parent } = this.state;
    const disabled = formStatus.pending;

    return (
      <Row>
        <Data dataName="client" /> {/* fetch meta data */}
        <List noRender listName="languages" url="/api/languages.json" list={languages} />
        <Col xs={12}>
          <FormErrors formStatus={formStatus} additionalErrors={formatEntityErrorsForDisplay(entity, messages)} />

          {entityLoaded ? (
            <form method="post" onSubmit={this.formSubmit}>
              <div className="panel panel-transparent">
                <Tabs
                  tabStyle="arrow"
                  tabResponsiveStyle="stack"
                  tabs={[
                    messages.user + ' (' + messages.basic_client_settings + ')',
                    messages.profile,
                    messages.financial,
                    messages.roles,
                  ]}
                  tabContent={[
                    <React.Fragment key="first">
                      <div className="panel panel-transparent">
                        <div className="panel-body">
                          <h4 className="new-client form-group-title">{messages.formGroup.basic_information}</h4>
                          <div className="form-group-attached">
                            {this.props.clientData ? (
                              <h3>{this.props.clientData.profile.company}</h3>
                            ) : (
                              <FormAutocomplete
                                label={messages.company + ' -'}
                                name="parent"
                                onChange={(name, value) => {
                                  this.updateParent(name, value);
                                  this.updateEntity('company', value?.profile?.company, ['profile']);
                                }}
                                value={clientSingleOptionFormatter(parent)}
                                autocompleteType="customer"
                                // urlFilters={{ type: 'customer' }} // we can use it also but with autocomplete type "client"
                                disabled={disabled}
                                returnValueObject
                                clearable
                                className="company"
                              />
                            )}
                            <FormInput
                              label={messages.email}
                              name="email"
                              value={entity.getProfile().getEmail()}
                              meta={entity.getProfile().getEmailMetaInformation()}
                              validation={entity.getProfile().getEmailValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['profile']);
                              }}
                              disabled={disabled}
                              className="email"
                            />

                            {may('ROLE_ADMIN', user.roles) && (
                              <div className="form-group form-group-default padding-10">
                                <FormSwitchery
                                  name="isActive"
                                  checked={entity.getIsActive()}
                                  value
                                  label={messages.is_active}
                                  onChange={(propertyName, value) => {
                                    this.updateEntity(propertyName, value);
                                  }}
                                  className="isActive"
                                />
                              </div>
                            )}
                          </div>

                          <h4 className="new-client form-group-title">{messages.formGroup.person_information}</h4>
                          <div className="form-group-attached">
                            <FormAutocomplete
                              label={messages.salutation}
                              name="salutation"
                              value={entity.getProfile().getSalutation()}
                              meta={entity.getProfile().getSalutationMetaInformation()}
                              validation={entity.getProfile().getSalutationValidationResult()}
                              onChange={this.updateSalutation}
                              options={getSalutationOptions(messages)}
                              disabled={disabled}
                              className="salutation"
                            />

                            <FormInput
                              label={messages.first_name}
                              name="firstName"
                              value={entity.getProfile().getFirstName()}
                              meta={entity.getProfile().getFirstNameMetaInformation()}
                              validation={entity.getProfile().getFirstNameValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['profile']);
                              }}
                              disabled={disabled}
                              className="firstName"
                            />
                            <FormInput
                              label={messages.last_name}
                              name="lastName"
                              value={entity.getProfile().getLastName()}
                              meta={entity.getProfile().getLastNameMetaInformation()}
                              validation={entity.getProfile().getLastNameValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['profile']);
                              }}
                              disabled={disabled}
                              className="lastName"
                            />
                          </div>

                          <h4 className="new-client form-group-title">{messages.formGroup.address_information}</h4>
                          <div className="form-group-attached">
                            <FormInput
                              label={messages.street}
                              name="street"
                              value={entity.getProfile().getStreet()}
                              meta={entity.getProfile().getStreetMetaInformation()}
                              validation={entity.getProfile().getStreetValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['profile']);
                              }}
                              disabled={disabled}
                              className="street"
                            />

                            <Row className="clearfix">
                              <Col sm={6}>
                                <FormInput
                                  label={messages.house_number}
                                  name="houseNumber"
                                  value={entity.getProfile().getHouseNumber()}
                                  meta={entity.getProfile().getHouseNumberMetaInformation()}
                                  validation={entity.getProfile().getHouseNumberValidationResult()}
                                  onChange={(propertyName, value) => {
                                    this.updateEntity(propertyName, value, ['profile']);
                                  }}
                                  disabled={disabled}
                                  className="houseNumber"
                                />
                              </Col>
                              <Col sm={6}>
                                <FormInput
                                  label={messages.apartment_number}
                                  name="apartmentNumber"
                                  value={entity.getProfile().getApartmentNumber()}
                                  meta={entity.getProfile().getApartmentNumberMetaInformation()}
                                  validation={entity.getProfile().getApartmentNumberValidationResult()}
                                  onChange={(propertyName, value) => {
                                    this.updateEntity(propertyName, value, ['profile']);
                                  }}
                                  disabled={disabled}
                                  className="apartmentNumber"
                                />
                              </Col>
                              <Col sm={12}>
                                <FormInput
                                  label={messages.address_line2}
                                  name="addressLine2"
                                  value={entity.getProfile().getAddressLine2()}
                                  meta={entity.getProfile().getAddressLine2MetaInformation()}
                                  validation={entity.getProfile().getAddressLine2ValidationResult()}
                                  onChange={(propertyName, value) => {
                                    this.updateEntity(propertyName, value, ['profile']);
                                  }}
                                  disabled={disabled}
                                  className="addressLine2"
                                />
                              </Col>
                            </Row>

                            <Row className="clearfix">
                              <Col sm={6}>
                                <FormInput
                                  label={messages.zip_code}
                                  name="zipCode"
                                  value={entity.getProfile().getZipCode()}
                                  meta={entity.getProfile().getZipCodeMetaInformation()}
                                  validation={entity.getProfile().getZipCodeValidationResult()}
                                  onChange={(propertyName, value) => {
                                    this.updateEntity(propertyName, value, ['profile']);
                                  }}
                                  disabled={disabled}
                                  className="zipCode"
                                />
                              </Col>
                              <Col sm={6}>
                                <FormInput
                                  label={messages.city}
                                  name="city"
                                  value={entity.getProfile().getCity()}
                                  meta={entity.getProfile().getCityMetaInformation()}
                                  validation={entity.getProfile().getCityValidationResult()}
                                  onChange={(propertyName, value) => {
                                    this.updateEntity(propertyName, value, ['profile']);
                                  }}
                                  disabled={disabled}
                                  className="city"
                                />
                              </Col>
                            </Row>

                            <FormAutocomplete
                              label={messages.country}
                              name="country"
                              value={entity.getProfile().getCountry()}
                              meta={entity.getProfile().getCountryMetaInformation()}
                              validation={entity.getProfile().getCountryValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['profile']);
                              }}
                              autocompleteType="country"
                              complete
                              disabled={disabled}
                              className="country"
                            />
                          </div>

                          <h4 className="new-client form-group-title">{messages.formGroup.contact_data}</h4>
                          <div className="form-group-attached">
                            <FormInput
                              label={messages.phone}
                              name="phone"
                              value={entity.getProfile().getPhone()}
                              meta={entity.getProfile().getPhoneMetaInformation()}
                              validation={entity.getProfile().getPhoneValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['profile']);
                              }}
                              disabled={disabled}
                              infoText={messages.client_settings_info_text.info_text_phone}
                              className="phone"
                            />
                          </div>

                          <h4 className="new-client form-group-title">
                            {messages.languages}{' '}
                            {languages.pending && <i className="m-l-10 fa fa-circle-notch fa-spin" />}
                          </h4>
                          <div className="form-group-attached">
                            <FormAutocomplete
                              label={messages.primary_language}
                              name="mainLanguage"
                              value={entity.getLang().getMainLanguage() || 'de'}
                              meta={entity.getLang().getMainLanguageMetaInformation()}
                              validation={entity.getLang().getMainLanguageValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['lang']);
                              }}
                              options={languages.data.list.map(language => {
                                return { value: language.id, label: language.name };
                              })}
                              complete
                              infoText={messages.client_settings_info_text.info_text_main_language}
                              className="mainLanguage"
                            />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>,
                    <ClientProfile
                      key="profile"
                      entityLoaded={entityLoaded}
                      entity={entity}
                      changeEntity={this.changeEntity}
                      disabled={formStatus.pending}
                      inModal
                      type={type}
                    />,
                    <ClientFinancial
                      key="financial"
                      typeOfUser="user"
                      entityLoaded={entityLoaded}
                      entity={entity}
                      changeEntity={this.changeEntity}
                      disabled={formStatus.pending}
                      inModal
                    />,
                    <ClientRoles
                      key="roles"
                      entityLoaded={entityLoaded}
                      entity={entity}
                      changeEntity={this.changeEntity}
                      disabled={formStatus.pending}
                      inModal
                      parent={parent}
                    />,
                  ]}
                />
                <div className="form-group form-group-default m-20">
                  <FormSwitchery
                    name="sendInvite"
                    label={
                      messages.send_invitation_with_username_and_password +
                      ' (' +
                      (entity.getProfile().getEmail() || '-') +
                      ')'
                    }
                    value
                    checked={!!(this.state.sendInvitation && entity.getProfile().getEmail())}
                    onChange={(name, value) => this.setState({ sendInvitation: value })}
                    disabled={!!(formStatus.pending || !entity.getProfile().getEmail())}
                  />
                </div>
                <div className="text-right m-t-15">
                  <Button
                    bsSize="lg"
                    disabled={formStatus.pending}
                    type="submit"
                    bsStyle={entityChanged ? 'primary' : 'default'}
                  >
                    <i className={'fa-fw ' + (formStatus.pending ? 'fa fa-circle-notch fa-spin' : 'fal fa-save')} />{' '}
                    {messages.save}
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <Loader />
          )}
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = state => {
  return {
    meta: state.entityMeta,
    user: state.login.user,
    languages: state.list.languages,
  };
};

export default withRouter(injectIntl(connect(mapStateToProps)(UserForm)));
